import React, { useEffect, useMemo, useState } from 'react';
import { Filter, Loader2, Plus, RefreshCw, RotateCcw } from 'lucide-react';
import { CategorySearch } from '../../../../components/Admin/Category/CategorySearch';
import LocationSearch from '../../../../components/Admin/SEO/LocationSearch';
import PaginationControls from '../../../../components/Shared/PaginationControls';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import { useToast } from '../../../../hooks/use-toast';
import {
  createProspectScrapeTask,
  createProspectScrapeTasksBulk,
  listProspectLocalities,
  listProspectScrapeTasks,
} from '../../../../services/AdminApis';
import api from '../../../../services/apiServices';

const STATUS_BADGE = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  IN_PROGRESS: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
};

const STATUS_OPTIONS = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'FAILED'];

const EMPTY_FILTERS = { country: 'ALL', category: null, location: null, status: 'ALL' };

const SKIP_REASON_LABEL = {
  location_not_found: 'location not found',
  wrong_country: 'wrong country',
  duplicate_task: 'task already exists',
};

const FilterBadge = ({ label, onClear }) => (
  <Badge variant="secondary" className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-200 cursor-default">
    {label}
    <span onClick={onClear} className="ml-1 cursor-pointer hover:text-red-500 font-bold">
      ×
    </span>
  </Badge>
);

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function ProspectScrapeTaskManager() {
  const { toast } = useToast();
  const [countries, setCountries] = useState([]);
  const [countryCode, setCountryCode] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [localities, setLocalities] = useState([]);
  const [localityId, setLocalityId] = useState('none');
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [bulkLocations, setBulkLocations] = useState([]);
  const [pagesPerRun, setPagesPerRun] = useState(1);
  const [saveAsParent, setSaveAsParent] = useState(true); // true → file under parent, false → exact leaf category
  const [serpProduct, setSerpProduct] = useState('GOOGLE_MAPS'); // GOOGLE_MAPS | GOOGLE_ORGANIC
  const [tasks, setTasks] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingLocalities, setLoadingLocalities] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formVersion, setFormVersion] = useState(0);

  // --- Filters & pagination (kept separate from the create-form state above) ---
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [isFiltered, setIsFiltered] = useState(false);
  const [stagedFilters, setStagedFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [filterVersion, setFilterVersion] = useState(0);

  const canSubmit = useMemo(
    () =>
      Boolean(countryCode) &&
      Boolean(selectedCategory?.id) &&
      (isBulkMode ? bulkLocations.length > 0 : Boolean(selectedLocation?.id)) &&
      Number(pagesPerRun) >= 1 &&
      !submitting,
    [countryCode, selectedCategory, selectedLocation, isBulkMode, bulkLocations, pagesPerRun, submitting]
  );

  const loadTasks = async () => {
    setLoadingTasks(true);
    try {
      const data = await listProspectScrapeTasks({
        page: pagination.page,
        limit: pagination.limit,
        country_code: isFiltered && appliedFilters.country !== 'ALL' ? appliedFilters.country : undefined,
        category_id: isFiltered ? appliedFilters.category?.id : undefined,
        location_id: isFiltered ? appliedFilters.location?.id : undefined,
        status: isFiltered && appliedFilters.status !== 'ALL' ? appliedFilters.status : undefined,
      });
      setTasks(data?.items || []);
      setPagination((prev) => ({ ...prev, total: data?.total || 0 }));
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to load scrape tasks.';
      toast({ variant: 'destructive', title: 'Unable to load tasks', description: detail });
    } finally {
      setLoadingTasks(false);
    }
  };

  useEffect(() => {
    const loadCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await api.get('/admin/common/active-countries');
        setCountries(response.data || []);
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Failed to load countries.';
        toast({ variant: 'destructive', title: 'Unable to load countries', description: detail });
      } finally {
        setLoadingCountries(false);
      }
    };

    loadCountries();
  }, []);

  useEffect(() => {
    loadTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, isFiltered, appliedFilters]);

  useEffect(() => {
    if (!selectedLocation?.id) {
      setLocalities([]);
      setLocalityId('none');
      return;
    }

    const loadLocalities = async () => {
      setLoadingLocalities(true);
      try {
        const data = await listProspectLocalities(selectedLocation.id);
        setLocalities(data || []);
        setLocalityId('none');
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Failed to load localities.';
        toast({ variant: 'destructive', title: 'Unable to load localities', description: detail });
      } finally {
        setLoadingLocalities(false);
      }
    };

    loadLocalities();
  }, [selectedLocation?.id]);

  const handleCountryChange = (value) => {
    setCountryCode(value);
    setSelectedCategory(null);
    setSelectedLocation(null);
    setLocalities([]);
    setLocalityId('none');
    setBulkLocations([]);
    setFormVersion((current) => current + 1);
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setLocalityId('none');
  };

  const handleBulkLocationAdd = (location) => {
    setBulkLocations((prev) => (prev.some((item) => item.id === location.id) ? prev : [...prev, location]));
    setFormVersion((current) => current + 1);
  };

  const handleBulkLocationRemove = (locationId) => {
    setBulkLocations((prev) => prev.filter((item) => item.id !== locationId));
  };

  const handleModeToggle = (bulk) => {
    setIsBulkMode(bulk);
    setSelectedLocation(null);
    setLocalities([]);
    setLocalityId('none');
    setBulkLocations([]);
    setFormVersion((current) => current + 1);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(stagedFilters);
    setIsFiltered(true);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleResetFilters = () => {
    setStagedFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setIsFiltered(false);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setFilterVersion((current) => current + 1);
  };

  // A category/location from one country is meaningless under another, so clear both.
  const handleFilterCountryChange = (value) => {
    setStagedFilters((prev) => ({ ...prev, country: value, category: null, location: null }));
    setFilterVersion((current) => current + 1);
  };

  const clearAppliedFilter = (key) => {
    const cleared = key === 'status' || key === 'country' ? 'ALL' : null;
    setStagedFilters((prev) => ({ ...prev, [key]: cleared }));
    setAppliedFilters((prev) => ({ ...prev, [key]: cleared }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const resetCreateForm = () => {
    setSelectedCategory(null);
    setSelectedLocation(null);
    setLocalities([]);
    setLocalityId('none');
    setBulkLocations([]);
    setPagesPerRun(1);
    setSaveAsParent(true);
    setSerpProduct('GOOGLE_MAPS');
    setFormVersion((current) => current + 1);
  };

  const afterTaskCreated = async () => {
    // The new task(s) sort to the top, so jump back to page 1 to make them visible.
    if (pagination.page === 1) {
      await loadTasks();
    } else {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      if (isBulkMode) {
        const result = await createProspectScrapeTasksBulk({
          country_code: countryCode,
          category_id: selectedCategory.id,
          location_ids: bulkLocations.map((location) => location.id),
          pages_per_run: Number(pagesPerRun),
          save_as_parent: saveAsParent,
          serp_product: serpProduct,
        });

        const { total_created: created = 0, total_skipped: skipped = 0, skipped: skippedItems = [] } = result || {};
        if (skipped > 0) {
          const bulkLocationById = new Map(bulkLocations.map((location) => [location.id, location]));
          const skippedSummary = skippedItems
            .map((item) => {
              const location = bulkLocationById.get(item.location_id);
              const label = location ? `${location.city}, ${location.state}` : `location ${item.location_id}`;
              return `${label} (${SKIP_REASON_LABEL[item.reason] || item.reason})`;
            })
            .join('; ');
          toast({
            variant: 'destructive',
            title: `Created ${created} of ${created + skipped} tasks`,
            description: `Skipped: ${skippedSummary}`,
          });
        } else {
          toast({
            title: `Created ${created} task${created === 1 ? '' : 's'}`,
            description: 'The prospect scrape tasks have been queued as pending.',
          });
        }
      } else {
        await createProspectScrapeTask({
          country_code: countryCode,
          category_id: selectedCategory.id,
          location_id: selectedLocation.id,
          locality_id: localityId === 'none' ? null : Number(localityId),
          pages_per_run: Number(pagesPerRun),
          save_as_parent: saveAsParent,
          serp_product: serpProduct,
        });

        toast({
          title: 'Task created',
          description: 'The prospect scrape task has been queued as pending.',
        });
      }

      resetCreateForm();
      await afterTaskCreated();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to create scrape task.';
      toast({ variant: 'destructive', title: 'Task not created', description: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">Prospect Scrape Tasks</h1>
        </div>
        <Button type="button" variant="outline" onClick={loadTasks} disabled={loadingTasks}>
          <RefreshCw size={16} className={`mr-2 ${loadingTasks ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card className="mb-6">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-lg">New Scrape Task</CardTitle>
          <div className="flex rounded-md border overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => handleModeToggle(false)}
              className={`px-3 py-1.5 ${!isBulkMode ? 'bg-purple-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              Single city
            </button>
            <button
              type="button"
              onClick={() => handleModeToggle(true)}
              className={`px-3 py-1.5 border-l ${isBulkMode ? 'bg-purple-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              Multiple cities
            </button>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid gap-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Country</Label>
                <Select value={countryCode} onValueChange={handleCountryChange} disabled={loadingCountries}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder={loadingCountries ? 'Loading countries...' : 'Select country'} />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country.country_code} value={country.country_code}>
                        {country.name} ({country.country_code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Pages Per Run</Label>
                <Input
                  type="number"
                  min="1"
                  value={pagesPerRun}
                  onChange={(event) => setPagesPerRun(event.target.value)}
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Prospect Filing</Label>
                <Select
                  value={saveAsParent ? 'parent' : 'leaf'}
                  onValueChange={(value) => setSaveAsParent(value === 'parent')}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent">Parent category (default)</SelectItem>
                    <SelectItem value="leaf">Exact category (leaf)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  How matched prospects are filed: under the parent, or this exact category.
                </p>
              </div>

              <div>
                <Label>Search Type</Label>
                <Select value={serpProduct} onValueChange={setSerpProduct}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GOOGLE_MAPS">Google Maps (default)</SelectItem>
                    <SelectItem value="GOOGLE_ORGANIC">Google Search (organic)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Organic results are AI-checked against the category before scraping.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <div className="mt-2">
                  {countryCode ? (
                    <CategorySearch
                      key={`category-${countryCode}-${formVersion}`}
                      locale={countryCode}
                      onSelect={setSelectedCategory}
                      typeFilter="child"
                      placeholder="Search child category..."
                    />
                  ) : (
                    <Input disabled placeholder="Select a country first" />
                  )}
                </div>
                {selectedCategory && (
                  <p className="text-xs text-gray-500 mt-1">
                    Selected: {selectedCategory.name} (ID: {selectedCategory.id})
                  </p>
                )}
              </div>

              <div>
                <Label>{isBulkMode ? 'Locations (add multiple)' : 'Location'}</Label>
                <div className="mt-2">
                  {countryCode ? (
                    <LocationSearch
                      key={`location-${countryCode}-${formVersion}`}
                      countryCode={countryCode}
                      onSelect={isBulkMode ? handleBulkLocationAdd : handleLocationSelect}
                      placeholder={isBulkMode ? 'Search and add a city...' : 'Search city...'}
                    />
                  ) : (
                    <Input disabled placeholder="Select a country first" />
                  )}
                </div>
                {!isBulkMode && selectedLocation && (
                  <p className="text-xs text-gray-500 mt-1">
                    Selected: {selectedLocation.city}, {selectedLocation.state} (ID: {selectedLocation.id})
                  </p>
                )}
                {isBulkMode && bulkLocations.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {bulkLocations.map((location) => (
                      <FilterBadge
                        key={location.id}
                        label={`${location.city}, ${location.state}`}
                        onClear={() => handleBulkLocationRemove(location.id)}
                      />
                    ))}
                  </div>
                )}
                {isBulkMode && bulkLocations.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">Add at least one city to create tasks for.</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
              {isBulkMode ? (
                <div>
                  <Label>Locality</Label>
                  <p className="text-xs text-gray-500 mt-2">
                    Not available in bulk mode — each task is created at the city level.
                  </p>
                </div>
              ) : (
                <div>
                  <Label>Locality</Label>
                  <Select
                    value={localityId}
                    onValueChange={setLocalityId}
                    disabled={!selectedLocation || loadingLocalities || localities.length === 0}
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue
                        placeholder={
                          loadingLocalities
                            ? 'Loading localities...'
                            : selectedLocation
                              ? 'Optional locality'
                              : 'Select a location first'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No locality</SelectItem>
                      {localities.map((locality) => (
                        <SelectItem key={locality.id} value={String(locality.id)}>
                          {locality.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedLocation && !loadingLocalities && localities.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">No localities found for this location.</p>
                  )}
                </div>
              )}

              <Button type="submit" disabled={!canSubmit} className="bg-purple-700 hover:bg-purple-800 text-white">
                {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Plus size={16} className="mr-2" />}
                {isBulkMode ? `Create ${bulkLocations.length || ''} Task${bulkLocations.length === 1 ? '' : 's'}` : 'Create Task'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Find Tasks</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4 flex-wrap">
            <div className="w-[180px]">
              <Label className="text-xs text-gray-500">Country</Label>
              <Select value={stagedFilters.country} onValueChange={handleFilterCountryChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="All countries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All countries</SelectItem>
                  {countries.map((country) => (
                    <SelectItem key={country.country_code} value={country.country_code}>
                      {country.name} ({country.country_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-[240px]">
              <Label className="text-xs text-gray-500">Category</Label>
              <div className="mt-1">
                {stagedFilters.country !== 'ALL' ? (
                  <CategorySearch
                    key={`filter-cat-${stagedFilters.country}-${filterVersion}`}
                    locale={stagedFilters.country}
                    onSelect={(category) => setStagedFilters((prev) => ({ ...prev, category }))}
                    typeFilter="child"
                    placeholder={stagedFilters.category?.name || 'Filter by category...'}
                  />
                ) : (
                  <Input disabled placeholder="Select a country first" />
                )}
              </div>
            </div>

            <div className="w-[240px]">
              <Label className="text-xs text-gray-500">Location</Label>
              <div className="mt-1">
                <LocationSearch
                  key={`filter-loc-${stagedFilters.country}-${filterVersion}`}
                  countryCode={stagedFilters.country === 'ALL' ? undefined : stagedFilters.country}
                  onSelect={(location) => setStagedFilters((prev) => ({ ...prev, location }))}
                  placeholder={stagedFilters.location?.city || 'Filter by city...'}
                />
              </div>
            </div>

            <div className="w-[180px]">
              <Label className="text-xs text-gray-500">Status</Label>
              <Select
                value={stagedFilters.status}
                onValueChange={(value) => setStagedFilters((prev) => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All statuses</SelectItem>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={handleApplyFilters}>
                <Filter size={16} className="mr-2" />
                Apply
              </Button>
              {isFiltered && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleResetFilters}
                  className="text-red-600 hover:bg-red-50"
                >
                  <RotateCcw size={16} className="mr-2" />
                  Reset
                </Button>
              )}
            </div>
          </div>

          {isFiltered && (
            <div className="flex items-center gap-2 flex-wrap pt-3 mt-3 border-t">
              <span className="text-xs font-semibold text-gray-500">Filtered by:</span>
              {appliedFilters.country !== 'ALL' && (
                <FilterBadge label={`Country: ${appliedFilters.country}`} onClear={() => clearAppliedFilter('country')} />
              )}
              {appliedFilters.category && (
                <FilterBadge
                  label={`Category: ${appliedFilters.category.name}`}
                  onClear={() => clearAppliedFilter('category')}
                />
              )}
              {appliedFilters.location && (
                <FilterBadge
                  label={`Location: ${appliedFilters.location.city}`}
                  onClear={() => clearAppliedFilter('location')}
                />
              )}
              {appliedFilters.status !== 'ALL' && (
                <FilterBadge
                  label={`Status: ${appliedFilters.status.replace('_', ' ')}`}
                  onClear={() => clearAppliedFilter('status')}
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Tasks</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>ID</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Locality</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Pages</TableHead>
                  <TableHead>Filing</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-sm text-gray-500 py-8">
                      {loadingTasks
                        ? 'Loading tasks...'
                        : isFiltered
                          ? 'No scrape tasks match these filters.'
                          : 'No scrape tasks found.'}
                    </TableCell>
                  </TableRow>
                )}
                {tasks.map((task) => (
                  <TableRow key={task.id}>
                    <TableCell className="font-medium">{task.id}</TableCell>
                    <TableCell>{task.country_code}</TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{task.category_name}</span>
                        <span className="text-xs text-gray-400">ID: {task.category_id}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span>{task.location_name || '-'}</span>
                        <span className="text-xs text-gray-400">ID: {task.location_id || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell>{task.locality_name || '-'}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_BADGE[task.status] || 'bg-gray-100 text-gray-700'}>
                        {task.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={task.serp_product === 'GOOGLE_ORGANIC' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-700'}>
                        {task.serp_product === 'GOOGLE_ORGANIC' ? 'Search' : 'Maps'}
                      </Badge>
                    </TableCell>
                    <TableCell>{task.pages_per_run}</TableCell>
                    <TableCell>
                      <Badge className={task.save_as_parent ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'}>
                        {task.save_as_parent ? 'Parent' : 'Leaf'}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(task.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {tasks.length > 0 && (
            <div className="pt-4 flex justify-between items-center gap-4">
              <p className="text-xs text-gray-500">
                Showing {tasks.length} of {pagination.total} tasks
              </p>
              <PaginationControls
                currentPage={pagination.page}
                totalPages={Math.ceil(pagination.total / pagination.limit)}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
